import { FormField } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { PlannerConfigEntityWithOptionalFields } from '@/services/configurator/PlannerConfigTypes';
import { Fragment, useEffect } from 'react';
import {
  Control,
  FieldPath,
  useController,
  UseFormReturn,
} from 'react-hook-form';
import { PhraseFormField } from './PhraseFormField';
import { PhraseFieldSection } from './PhraseFieldSection';

export type ToggleablePhraseFieldsProps = {
  sectionKey: FieldPath<PlannerConfigEntityWithOptionalFields>;
  sectionTitle: string;
  sectionDescription: string;
  phraseStringKeys: Array<string>;
  formControl: Control<PlannerConfigEntityWithOptionalFields>;
  phraseForm: UseFormReturn<PlannerConfigEntityWithOptionalFields>;
  disableFields?: boolean;
  sectionClassName?: string;
  fieldClassName?: string;
  syncedPhraseKeys?: Set<string>;
};

export function ToggleablePhraseFields({
  sectionKey,
  sectionTitle,
  sectionDescription,
  phraseStringKeys,
  formControl,
  phraseForm,
  disableFields,
  sectionClassName,
  fieldClassName,
  syncedPhraseKeys,
}: ToggleablePhraseFieldsProps) {
  const toggleController = useController({
    control: formControl,
    name: sectionKey,
  });

  const sectionEnabled = !!toggleController.field.value;

  useEffect(() => {
    // clear the fields on section toggling off
    if (sectionEnabled === false) {
      const phraseStrings = phraseForm.getValues('phraseStrings') ?? {};
      const filteredPhraseStrings = Object.fromEntries(
        Object.entries(phraseStrings).filter(
          ([phraseKey]) => !phraseStringKeys.includes(phraseKey),
        ),
      );

      phraseForm.setValue('phraseStrings', filteredPhraseStrings, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      for (const phraseKey of phraseStringKeys) {
        // Instead of settings its value to '' we are removing the key completely from the phraseForm
        // So that we dont send a key with a empty value to the backend
        phraseForm.unregister(`phraseStrings.${phraseKey}`);
      }
    }
  }, [sectionEnabled, phraseForm, phraseStringKeys]);

  const phraseFieldsectionClassName = `${
    !sectionEnabled ? 'w-[calc(50%-16px)]' : ''
  }${sectionClassName ? ` ${sectionClassName}` : ''}`;

  const renderHeaderItem = () => {
    return (
      <FormField
        control={formControl}
        name={sectionKey}
        disabled={disableFields}
        render={({ field }) => {
          return (
            <Switch
              disabled={disableFields}
              key={sectionKey}
              checked={!!field.value}
              onCheckedChange={(value) => field.onChange(value)}
              className='mr-4'
            />
          );
        }}
      />
    );
  };

  const renderSectionContents = () => {
    if (sectionEnabled) {
      return (
        <Fragment>
          {phraseStringKeys?.map((phraseKey: string) => (
            <Fragment key={phraseKey}>
              <PhraseFormField
                phraseKey={phraseKey}
                phraseFormControl={phraseForm.control}
                disabled={disableFields}
                className={fieldClassName}
                syncedPhraseKeys={syncedPhraseKeys}
              />
            </Fragment>
          ))}
        </Fragment>
      );
    }
    return <></>;
  };

  return (
    <PhraseFieldSection
      sectionTitle={sectionTitle}
      sectionDescription={sectionDescription}
      sectionClassName={phraseFieldsectionClassName}
      renderHeaderItem={renderHeaderItem}
      renderSectionContents={renderSectionContents}
      sectionDescriptionVisible={sectionEnabled}
    />
  );
}
